import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Form from 'react-bootstrap/Form';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FaRegSave } from 'react-icons/fa';
import { MdArrowBack } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from '../../toasts';
import DetailPageLoader from '../../components/DetailPageLoader/DetailPageLoader';
import PaymentReceiptStep from '../../components/PaymentReceiptStep/PaymentReceiptStep';
import {
	Button,
	EntityList,
	FormField,
	PageHead,
	Section,
	Select,
	controlClass,
	textareaClass,
} from '../../components/EntityList';
import debtService from '../../services/debt.service';
import PaymentService from '../../services/payment.service';
import { getError } from '../../utils';
import { useLabels } from '../../hooks/useLabels';

const REQUIRED_FIELD_MESSAGE = 'Este campo es obligatorio.';

const FieldError = ({ message }) =>
	message ? <p className='tw:mt-1 tw:mb-0 tw:text-[12px] tw:text-danger'>{message}</p> : null;

FieldError.propTypes = {
	message: PropTypes.string,
};

/**
 * Registers a payment against a single debt due. This was DebtDuePaymentModal;
 * the due is resolved from the debt's dues list (there is no single-due
 * endpoint) so the URL is reloadable, and the guards the modal's opener applied
 * are repeated here for direct URL entry.
 */
function DebtDuePayment() {
	const { t } = useLabels();
	const { id, dueId } = useParams();
	const navigate = useNavigate();

	const backTo = `/AdminScreen/debts/debt/${id}/cuotas`;

	const [loading, setLoading] = useState(true);
	const [due, setDue] = useState(null);
	const [saving, setSaving] = useState(false);
	const [amount, setAmount] = useState('');
	const [date, setDate] = useState(null);
	const [currency, setCurrency] = useState('');
	const [folder, setFolder] = useState('');
	const [observations, setObservations] = useState('');
	const [comprobante, setComprobante] = useState('');
	const [paidBy, setPaidBy] = useState('');
	const [paymentMethod, setPaymentMethod] = useState('');
	const [unpaidAmount, setUnpaidAmount] = useState(null);
	const [fieldErrors, setFieldErrors] = useState({});
	const [summaryError, setSummaryError] = useState('');
	const [createdPaymentId, setCreatedPaymentId] = useState(null);

	useEffect(() => {
		let isCurrent = true;
		const load = async () => {
			setLoading(true);
			try {
				const { data } = await debtService.getDuesForDebt(id);
				const found = (data || []).find((row) => row._id === dueId);
				if (!isCurrent) return;
				if (!found) {
					toast.error('Cuota no encontrada');
					navigate(backTo, { replace: true });
					return;
				}
				if (found.paid) {
					toast.info(t('msg.dueAlreadyPaid'));
					navigate(backTo, { replace: true });
					return;
				}
				if (found.billingStatus === 'pendingCount') {
					toast.info(t('msg.confirmCountBeforePayment'));
					navigate(backTo, { replace: true });
					return;
				}
				setDue(found);
				setAmount(found.unpaidAmount != null ? found.unpaidAmount : '');
				setUnpaidAmount(found.unpaidAmount ?? null);
				setCurrency(found.debt?.currency || '');
				setFolder(found.debt?.owner?.folder || '');
			} catch (err) {
				if (isCurrent) toast.error(getError(err));
			} finally {
				if (isCurrent) setLoading(false);
			}
		};
		load();
		return () => {
			isCurrent = false;
		};
	}, [id, dueId]);

	const clearFieldError = useCallback((field) => {
		setFieldErrors((prevErrors) => {
			if (!prevErrors[field]) return prevErrors;
			const nextErrors = { ...prevErrors };
			delete nextErrors[field];
			return nextErrors;
		});
		setSummaryError('');
	}, []);

	const handleAmountChange = (e) => {
		const val = parseFloat(e.target.value);
		if (e.target.value === '') {
			setAmount('');
		} else if (!isNaN(val) && (unpaidAmount == null || val <= unpaidAmount)) {
			setAmount(val);
		}
		clearFieldError('amount');
	};

	const uploadComprobante = async () => {
		if (!comprobante || comprobante.length === 0) {
			toast.warning(t('msg.selectFile'));
			return;
		}
		const file = comprobante[0];
		const formData = new FormData();
		const r = Math.random().toString(36).substring(7);
		formData.append('file', file, (file.name || '').replaceAll(' ', '') + r);
		try {
			const response = await PaymentService.uploadComprobante(formData);
			setComprobante(response.data);
			toast.success(t('msg.fileUploaded'));
		} catch (err) {
			toast.error(getError(err));
		}
	};

	const FIELD_LABELS = {
		amount: t('field.amount'),
		date: t('field.date'),
	};

	const getFieldErrors = () => {
		const errors = {};
		if (!date) errors.date = REQUIRED_FIELD_MESSAGE;
		if (amount === '' || parseFloat(amount) <= 0) errors.amount = REQUIRED_FIELD_MESSAGE;
		return errors;
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		if (saving) return;

		const errors = getFieldErrors();
		const missingFields = Object.keys(errors);
		if (missingFields.length > 0) {
			setFieldErrors(errors);
			const missingLabels = missingFields.map((field) => FIELD_LABELS[field] || field);
			setSummaryError(`${t('msg.completeFields')}: ${missingLabels.join(', ')}`);
			return;
		}
		setFieldErrors({});
		setSummaryError('');

		setSaving(true);
		try {
			const payload = {
				amount: parseFloat(amount),
				date,
				debt: due.debt._id,
				debtDue: due._id,
				currency,
				folder,
				observations,
				paidBy,
				paymentMethod,
			};
			if (comprobante && comprobante._id) {
				payload.comprobante = comprobante._id;
			}
			const { data } = await PaymentService.create(payload);
			toast.success(t('msg.paymentCreated'));
			setCreatedPaymentId(data.payment._id);
		} catch (err) {
			toast.error(getError(err));
		} finally {
			setSaving(false);
		}
	};

	const heading = createdPaymentId
		? t('title.paymentRegistered')
		: `${t('title.registerPayment')}${due?.customId ? ` — ${due.customId}` : ''}`;

	return (
		<EntityList>
			<Helmet>
				<title>{t('title.registerPayment')}</title>
			</Helmet>
			<PageHead
				breadcrumb={[
					{ label: 'Inicio', to: '/AdminScreen' },
					{ label: t('entity.debt.plural'), to: '/AdminScreen/debts' },
					{ label: due?.debt?.customId || t('entity.debt'), to: backTo },
					{ label: t('title.registerPayment') },
				]}
				title={heading}
				subtitle={
					createdPaymentId
						? undefined
						: 'Completá los datos para registrar el cobro de la cuota.'
				}
			/>

			{loading ? (
				<DetailPageLoader entityLabel='editor' />
			) : createdPaymentId ? (
				<PaymentReceiptStep paymentId={createdPaymentId} onDone={() => navigate(backTo)} />
			) : (
				<Form id='debt-due-payment-form' onSubmit={handleSubmit} className='tw:space-y-4'>
					<div className='tw:sticky tw:top-14 tw:z-20 tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3 tw:rounded-panel tw:border tw:border-border tw:bg-surface/95 tw:px-5 tw:py-3.5 tw:shadow-sm tw:backdrop-blur'>
						<div className='tw:text-[12.5px] tw:text-ink-2'>
							{due?.customId ? `Cuota ${due.customId}` : 'Cuota'}
						</div>
						<div className='tw:flex tw:gap-2'>
							<Button icon={<MdArrowBack />} onClick={() => navigate(backTo)} disabled={saving}>
								{t('action.cancel')}
							</Button>
							<Button variant='primary' icon={<FaRegSave />} type='submit' disabled={saving}>
								{saving ? 'Guardando...' : t('action.add')}
							</Button>
						</div>
					</div>

					{summaryError && (
						<div className='tw:rounded-[7px] tw:border tw:border-danger tw:bg-danger-soft tw:px-4 tw:py-3 tw:text-[13px] tw:text-danger'>
							{summaryError}
						</div>
					)}

					<Section title={t('title.registerPayment')}>
						<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
							<FormField id='debt-due-payment-amount' label={t('field.amount')} required>
								<Form.Control
									id='debt-due-payment-amount'
									type='number'
									className={controlClass}
									value={amount}
									onChange={handleAmountChange}
									min={0}
									max={unpaidAmount != null ? unpaidAmount : undefined}
									disabled={saving}
									onInput={(e) => {
										if (unpaidAmount != null && parseFloat(e.target.value) > unpaidAmount) {
											e.target.value = unpaidAmount;
										}
									}}
								/>
								{unpaidAmount != null && (
									<p className='tw:mt-1 tw:mb-0 tw:text-[12px] tw:text-ink-3'>
										{t('msg.maximum')}: {unpaidAmount}
									</p>
								)}
								<FieldError message={fieldErrors.amount} />
							</FormField>
							<FormField id='debt-due-payment-date' label={t('field.date')} required>
								<DatePicker
									id='debt-due-payment-date'
									selected={date}
									onChange={(d) => {
										setDate(d);
										clearFieldError('date');
									}}
									dateFormat='dd-MM-yyyy'
									className={`form-control ${controlClass}`}
									placeholderText='dd-mm-yyyy'
									disabled={saving}
								/>
								<FieldError message={fieldErrors.date} />
							</FormField>
							<FormField id='debt-due-payment-currency' label={t('field.currency')}>
								<Select
									id='debt-due-payment-currency'
									options={['USD', 'UYU']}
									value={currency}
									onChange={(e) => setCurrency(e.target.value)}
									placeholder='Elegí Moneda'
									disabled={saving}
								/>
							</FormField>
							<FormField id='debt-due-payment-folder' label={t('field.folder')}>
								<Form.Control
									id='debt-due-payment-folder'
									className={controlClass}
									value={folder}
									onChange={(e) => setFolder(e.target.value)}
									disabled={saving}
								/>
							</FormField>
							<FormField id='debt-due-payment-paid-by' label={t('form.paidBy')}>
								<Form.Control
									id='debt-due-payment-paid-by'
									className={controlClass}
									value={paidBy}
									onChange={(e) => setPaidBy(e.target.value)}
									placeholder={t('form.placeholder.payerName')}
									disabled={saving}
								/>
							</FormField>
							<FormField id='debt-due-payment-method' label={t('form.paymentMedium')}>
								<Form.Control
									id='debt-due-payment-method'
									className={controlClass}
									value={paymentMethod}
									onChange={(e) => setPaymentMethod(e.target.value)}
									placeholder={t('form.placeholder.paymentMedium')}
									disabled={saving}
								/>
							</FormField>
						</div>

						<div className='tw:mt-4 tw:flex tw:items-end tw:gap-3'>
							<FormField id='debt-due-payment-comprobante' label={t('form.comprobante')} className='tw:flex-1'>
								<Form.Control
									id='debt-due-payment-comprobante'
									type='file'
									className={controlClass}
									onChange={(e) => setComprobante(e.target.files)}
									disabled={saving}
								/>
							</FormField>
							<Button onClick={uploadComprobante} disabled={saving}>
								{t('action.upload')}
							</Button>
						</div>

						<FormField
							id='debt-due-payment-observations'
							label={t('field.observations')}
							className='tw:mt-4'
						>
							<Form.Control
								id='debt-due-payment-observations'
								as='textarea'
								rows={3}
								className={textareaClass}
								value={observations}
								onChange={(e) => setObservations(e.target.value)}
								disabled={saving}
							/>
						</FormField>
					</Section>
				</Form>
			)}
		</EntityList>
	);
}

export default DebtDuePayment;
